import React from 'react';
import {
  Box,
  Flex,
  DeprecatedIcon,
  TextInput,
  SelectMenu,
  OptionType,
} from '@fullstory/components';
import { isEqual } from 'lodash';
import { ControlValue, FilterOptionValueType, Value } from '@fullstory/redux';
import { intl } from '@fullstory/intl';

export const defaultControlValue: ControlValue = {
  filter: 'active',
  search: '',
};

export const defaultResourceFilters: FilterOptionValueType[] = [
  'active',
  'archived',
  'recent',
  'starred',
  'mine',
  'hasInsights',
];

export const defaultResourceOptions: OptionType<FilterOptionValueType>[] = [
  {
    key: 'active',
    label: intl.formatMessage({
      defaultMessage: 'Active',
      id: 'qK/kCb',
      description: 'Filter for active resources',
    }),
    value: 'active',
  },
  {
    key: 'archived',
    label: intl.formatMessage({
      defaultMessage: 'Archived',
      id: 'eOtAhT',
      description: 'Filter for archived resources',
    }),
    value: 'archived',
  },
  {
    key: 'recent',
    label: intl.formatMessage({
      defaultMessage: 'Recent',
      id: 'poczoE',
      description: 'Filter for recent resources',
    }),
    value: 'recent',
  },
  {
    key: 'starred',
    label: intl.formatMessage({
      defaultMessage: 'Starred',
      id: '7efGQr',
      description: 'Filter for starred resources',
    }),
    value: 'starred',
  },
  {
    key: 'mine',
    label: intl.formatMessage({
      defaultMessage: 'Created By Me',
      id: 'Ta0Jiw',
      description: 'Filter for resources created by me',
    }),
    value: 'mine',
  },
  {
    key: 'private',
    label: intl.formatMessage({
      defaultMessage: 'Private',
      id: 'yuJZPe',
      description: 'Filter for my private resources',
    }),
    value: 'private',
  },
  {
    key: 'hasInsights',
    label: intl.formatMessage({
      defaultMessage: 'Has Insights',
      id: 'hGv7C8',
      description: 'Filter for objects with recent insights',
    }),
    value: 'hasInsights',
  },
];

export const defaultStringToFilter = (string: string): FilterOptionValueType | undefined =>
  defaultResourceFilters.find(d => d === string);

export interface ResourceListControlsProps<T> {
  filters: OptionType<T>[];
  value: Value<T>;
  onChange: (value: Value<T>) => void;
  placeholder?: string;
}

export function ResourceListControls<T>({
  filters,
  value,
  onChange,
  placeholder,
}: ResourceListControlsProps<T>) {
  return (
    <Box data-component="ResourceListControls" padding={16}>
      <Flex>
        <Box width={200} paddingLeft={8} paddingRight={8}>
          <SelectMenu
            fullWidth
            variant="fill-builder"
            onChange={o => {
              if (o)
                onChange({
                  ...value,
                  filter: o.value,
                });
            }}
            options={filters}
            selected={filters.find(o => isEqual(o.value, value.filter)) || filters[0]}
          />
        </Box>
        <Box flexGrow={1} paddingLeft={8} paddingRight={8}>
          <TextInput
            value={value.search}
            onChange={e =>
              onChange({
                ...value,
                search: e.target.value,
              })
            }
            placeholder={
              placeholder ||
              intl.formatMessage({ defaultMessage: 'Start typing to search...', id: '0MbifP' })
            }
            leftAdornment={
              <Box paddingLeft={8}>
                <DeprecatedIcon
                  name="search"
                  color="pink-base"
                  size={20}
                  grammarUIPropOverrides={{ color: 'base-objects-disabled' }}
                />
              </Box>
            }
          />
        </Box>
      </Flex>
    </Box>
  );
}
